import type {
  WorkspaceDetail,
  WorkspaceEdge,
  WorkspaceNode,
  WorkspaceNodePosition,
  WorkspaceNodeStatus,
} from "../../../types/workspace";
import { isFederatedWorkspaceNodeId } from "../../../types/workspace";
import { isPersistedWorkspaceResourceId } from "../../../utils/workspacePersistIds";

export {
  isPersistedWorkspaceResourceId,
  sanitizeSimulateReportId,
  sanitizeWorkspaceSimulateAttachFields,
} from "../../../utils/workspacePersistIds";

const OPTIMISTIC_REPORT_NODE_PREFIX = "optimistic-report-";
const OPTIMISTIC_SIMULATION_NODE_PREFIX = "optimistic-simulation-";
const PENDING_ENTITY_PREFIX = "pending-";

export function isOptimisticWorkspaceReportNodeId(nodeId: string): boolean {
  return nodeId.startsWith(OPTIMISTIC_REPORT_NODE_PREFIX);
}

export function isOptimisticWorkspaceSimulationNodeId(nodeId: string): boolean {
  return nodeId.startsWith(OPTIMISTIC_SIMULATION_NODE_PREFIX);
}

export function isOptimisticWorkspaceNodeId(nodeId: string): boolean {
  return (
    isOptimisticWorkspaceReportNodeId(nodeId) ||
    isOptimisticWorkspaceSimulationNodeId(nodeId)
  );
}

export function isPendingOptimisticReportEntityId(entityId: string): boolean {
  return entityId.startsWith(PENDING_ENTITY_PREFIX);
}

export function isPendingOptimisticSimulationEntityId(entityId: string): boolean {
  return entityId.startsWith(PENDING_ENTITY_PREFIX);
}

/** Prefer the real workspace node id when a Flow node still uses an optimistic-* id. */
export function workspacePersistNodeId(node: WorkspaceNode): string {
  // Federated strategy canvas: write against the account-canvas node UUID.
  const sourceNodeId = node.sourceNodeId?.trim();
  if (sourceNodeId && isPersistedWorkspaceResourceId(sourceNodeId)) {
    return sourceNodeId;
  }
  const serverNodeId = node.meta?.serverNodeId?.trim();
  if (serverNodeId && isPersistedWorkspaceResourceId(serverNodeId)) {
    return serverNodeId;
  }
  return node.id;
}

/**
 * Account workspace id for register/save attach. On strategy federated canvases,
 * use the node's provenance workspace — never the synthetic strategy workspace id.
 */
export function workspacePersistWorkspaceId(
  node: WorkspaceNode,
  fallbackWorkspaceId: string
): string {
  const sourceWorkspaceId = node.sourceWorkspaceId?.trim();
  if (sourceWorkspaceId && isPersistedWorkspaceResourceId(sourceWorkspaceId)) {
    return sourceWorkspaceId;
  }
  return fallbackWorkspaceId.trim();
}

/** True when a position/layout persist would still send an optimistic-* or federated node id. */
export function shouldSkipWorkspacePositionPersist(node: WorkspaceNode): boolean {
  const persistId = workspacePersistNodeId(node);
  return (
    !isPersistedWorkspaceResourceId(persistId) ||
    isOptimisticWorkspaceNodeId(persistId) ||
    isFederatedWorkspaceNodeId(persistId)
  );
}

/** Pending optimistic placeholders (report generate / sim rerun) before the real entity exists. */
export function isPendingOptimisticWorkspaceNode(node: WorkspaceNode): boolean {
  if (
    isOptimisticWorkspaceReportNodeId(node.id) &&
    isPendingOptimisticReportEntityId(node.entityId)
  ) {
    return true;
  }
  if (
    isOptimisticWorkspaceSimulationNodeId(node.id) &&
    isPendingOptimisticSimulationEntityId(node.entityId)
  ) {
    return true;
  }
  return false;
}

function parentByTarget(edges: WorkspaceEdge[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const edge of edges) map.set(edge.targetNodeId, edge.sourceNodeId);
  return map;
}

export function stripOptimisticWorkspaceReportNodes(
  detail: WorkspaceDetail
): WorkspaceDetail {
  // Only pending placeholders — adopted optimistic-* Flow ids still represent
  // real server nodes for this session; wiping them drops lineage edges.
  const pendingIds = new Set(
    detail.nodes
      .filter(
        (node) =>
          isOptimisticWorkspaceReportNodeId(node.id) &&
          isPendingOptimisticReportEntityId(node.entityId)
      )
      .map((node) => node.id)
  );
  if (pendingIds.size === 0) return detail;
  return {
    ...detail,
    nodes: detail.nodes.filter((node) => !pendingIds.has(node.id)),
    edges: detail.edges.filter(
      (edge) => !pendingIds.has(edge.sourceNodeId) && !pendingIds.has(edge.targetNodeId)
    ),
  };
}

export function stripOptimisticWorkspaceSimulationNodes(
  detail: WorkspaceDetail
): WorkspaceDetail {
  // Only pending placeholders — adopted optimistic-* sims must stay so
  // Generate does not strip prior session sims and their edges.
  const pendingIds = new Set(
    detail.nodes
      .filter(
        (node) =>
          isOptimisticWorkspaceSimulationNodeId(node.id) &&
          isPendingOptimisticSimulationEntityId(node.entityId)
      )
      .map((node) => node.id)
  );
  if (pendingIds.size === 0) return detail;
  return {
    ...detail,
    nodes: detail.nodes.filter((node) => !pendingIds.has(node.id)),
    edges: detail.edges.filter(
      (edge) => !pendingIds.has(edge.sourceNodeId) && !pendingIds.has(edge.targetNodeId)
    ),
  };
}

/** Remove canvas nodes (and their edges) immediately — used after a successful break-link. */
export function removeWorkspaceNodeFromDetail(
  detail: WorkspaceDetail,
  nodeId: string | Iterable<string>
): WorkspaceDetail {
  const removeIds = new Set(typeof nodeId === "string" ? [nodeId] : Array.from(nodeId));
  if (removeIds.size === 0) return detail;
  return {
    ...detail,
    nodes: detail.nodes.filter((node) => !removeIds.has(node.id)),
    edges: detail.edges.filter(
      (edge) => !removeIds.has(edge.sourceNodeId) && !removeIds.has(edge.targetNodeId)
    ),
  };
}

type OptimisticMergeKind = "report" | "simulation";

function isOptimisticIdForKind(nodeId: string, kind: OptimisticMergeKind): boolean {
  return kind === "report"
    ? isOptimisticWorkspaceReportNodeId(nodeId)
    : isOptimisticWorkspaceSimulationNodeId(nodeId);
}

function isPendingEntityForKind(entityId: string, kind: OptimisticMergeKind): boolean {
  return kind === "report"
    ? isPendingOptimisticReportEntityId(entityId)
    : isPendingOptimisticSimulationEntityId(entityId);
}

function nodeMatchesMergeKind(node: WorkspaceNode, kind: OptimisticMergeKind): boolean {
  if (kind === "report") return node.type === "report";
  return node.type === "simulation" || node.type === "combined_simulation";
}

function defaultOptimisticTitle(kind: OptimisticMergeKind): string {
  return kind === "report" ? "New report" : "New simulation";
}

/**
 * Soft refreshes can briefly return nodes with an empty edge list (register /
 * reparent races). Prefer remapping the previous lineage onto server node ids
 * over wiping connectors until the next good payload.
 */
function remapPreviousEdgesOntoServerNodes(
  previous: WorkspaceDetail,
  server: WorkspaceDetail
): WorkspaceEdge[] {
  const serverIds = new Set(server.nodes.map((node) => node.id));
  const prevById = new Map(previous.nodes.map((node) => [node.id, node]));
  const serverIdByTypeEntity = new Map(
    server.nodes.map((node) => [`${node.type}:${node.entityId.trim()}`, node.id] as const)
  );

  const rewriteId = (id: string): string | null => {
    if (serverIds.has(id)) return id;
    const prev = prevById.get(id);
    if (!prev) return null;
    const serverNodeId = prev.meta?.serverNodeId?.trim();
    if (serverNodeId && serverIds.has(serverNodeId)) return serverNodeId;
    const entityId = prev.entityId.trim();
    if (!entityId || isPendingEntityForKind(entityId, "simulation") || isPendingEntityForKind(entityId, "report")) {
      return null;
    }
    return serverIdByTypeEntity.get(`${prev.type}:${entityId}`) ?? null;
  };

  const edges: WorkspaceEdge[] = [];
  const seen = new Set<string>();
  for (const edge of previous.edges) {
    const sourceNodeId = rewriteId(edge.sourceNodeId);
    const targetNodeId = rewriteId(edge.targetNodeId);
    if (!sourceNodeId || !targetNodeId) continue;
    const key = `${sourceNodeId}->${targetNodeId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    edges.push({
      ...edge,
      id: edge.id || key,
      sourceNodeId,
      targetNodeId,
    });
  }
  return edges;
}

/**
 * Soft refreshes replace the whole graph from the server and would otherwise drop
 * in-flight optimistic placeholders (flash off → back on while generating).
 *
 * Rules:
 * - Pending placeholders stay until a new server node of the same kind covers them.
 * - Stamped/adopted optimistic Flow ids remapped onto matching server nodes
 *   (stable React Flow identity).
 * - Stamped optimistics whose entity is gone from the server are dropped — never
 *   resurrected (e.g. after “Remove from workspace”).
 */
function mergeOptimisticKind(
  previous: WorkspaceDetail,
  server: WorkspaceDetail,
  kind: OptimisticMergeKind
): WorkspaceDetail {
  const optimisticNodes = previous.nodes.filter(
    (node) => nodeMatchesMergeKind(node, kind) && isOptimisticIdForKind(node.id, kind)
  );
  if (optimisticNodes.length === 0) return server;

  const previousParentByTarget = parentByTarget(previous.edges);
  const serverParentByTarget = parentByTarget(server.edges);

  const pendingToRetain: WorkspaceNode[] = [];
  for (const opt of optimisticNodes) {
    if (!isPendingEntityForKind(opt.entityId, kind)) {
      // Stamped/adopted: only remapped when still on the server — never keep a
      // ghost when the entity was removed from the workspace.
      continue;
    }

    const parentId = previousParentByTarget.get(opt.id);
    const siblingOnServer =
      parentId != null &&
      server.nodes.some(
        (node) =>
          nodeMatchesMergeKind(node, kind) &&
          serverParentByTarget.get(node.id) === parentId &&
          !previous.nodes.some(
            (prev) =>
              nodeMatchesMergeKind(prev, kind) &&
              !isOptimisticIdForKind(prev.id, kind) &&
              prev.entityId === node.entityId
          )
      );

    if (siblingOnServer) continue;
    pendingToRetain.push(opt);
  }

  const idRewrite = new Map<string, string>();
  const claimed = new Set<string>();
  const nodes = server.nodes.map((node) => {
    if (!nodeMatchesMergeKind(node, kind)) return node;
    const match = optimisticNodes.find((opt) => {
      if (claimed.has(opt.id)) return false;
      if (!isPendingEntityForKind(opt.entityId, kind)) {
        return opt.entityId === node.entityId;
      }
      // Pending match only when we are about to drop it (covered by this node)
      // and are not still retaining it.
      if (pendingToRetain.some((retained) => retained.id === opt.id)) return false;
      return previousParentByTarget.get(opt.id) === serverParentByTarget.get(node.id);
    });
    if (!match) return node;
    claimed.add(match.id);
    idRewrite.set(node.id, match.id);
    const keepSlot = isPendingEntityForKind(match.entityId, kind);
    return {
      ...node,
      id: match.id,
      position: keepSlot ? match.position : node.position,
      meta: {
        ...match.meta,
        ...node.meta,
        title: node.meta?.title ?? match.meta?.title ?? defaultOptimisticTitle(kind),
        status: node.meta?.status ?? match.meta?.status,
        subtitle: node.meta?.subtitle ?? match.meta?.subtitle,
        serverNodeId: node.id,
      },
    };
  });

  const edges = server.edges.map((edge) => ({
    ...edge,
    sourceNodeId: idRewrite.get(edge.sourceNodeId) ?? edge.sourceNodeId,
    targetNodeId: idRewrite.get(edge.targetNodeId) ?? edge.targetNodeId,
  }));

  const nodeIds = new Set(nodes.map((node) => node.id));
  for (const opt of pendingToRetain) {
    if (nodeIds.has(opt.id)) continue;
    nodes.push(opt);
    nodeIds.add(opt.id);
    const parentId = previousParentByTarget.get(opt.id);
    if (parentId) {
      edges.push({
        id: `${opt.id}-edge`,
        sourceNodeId: parentId,
        targetNodeId: opt.id,
      });
    }
  }

  return { ...server, nodes, edges };
}

export function mergeWorkspaceDetailPreservingOptimistic(
  previous: WorkspaceDetail | null,
  server: WorkspaceDetail
): WorkspaceDetail {
  if (!previous) return server;
  // Register / reparent soft-refresh races sometimes omit edges while nodes
  // remain — keep prior lineage remapped onto server ids instead of blanking.
  const serverGraph =
    server.edges.length === 0 &&
    previous.edges.length > 0 &&
    server.nodes.length > 0
      ? {
          ...server,
          edges: remapPreviousEdgesOntoServerNodes(previous, server),
        }
      : server;
  const withReports = mergeOptimisticKind(previous, serverGraph, "report");
  return mergeOptimisticKind(previous, withReports, "simulation");
}

/** Stamp the placeholder with the real report id so refresh can adopt it cleanly. */
export function stampOptimisticReportEntity(
  detail: WorkspaceDetail,
  args: { reportId: string; title?: string; status?: string }
): WorkspaceDetail {
  const reportId = args.reportId.trim();
  if (!reportId) return detail;

  let stamped = false;
  const nodes = detail.nodes.map((node) => {
    if (!isOptimisticWorkspaceReportNodeId(node.id)) return node;
    if (!isPendingOptimisticReportEntityId(node.entityId)) return node;
    if (stamped) return node;
    stamped = true;
    const status =
      args.status === "processing" || args.status === "draft" || args.status === "pending"
        ? "processing"
        : args.status === "failed" || args.status === "error"
          ? "failed"
          : node.meta?.status ?? "processing";
    return {
      ...node,
      entityId: reportId,
      meta: {
        ...node.meta,
        title: args.title?.trim() || node.meta?.title || "New report",
        status,
      },
    };
  });

  return stamped ? { ...detail, nodes } : detail;
}

export function insertOptimisticWorkspaceReportNode(
  detail: WorkspaceDetail,
  args: {
    title: string;
    parentNodeId: string;
    position: WorkspaceNodePosition;
  }
): { detail: WorkspaceDetail; nodeId: string } {
  const cleaned = stripOptimisticWorkspaceReportNodes(detail);
  const nodeId = `${OPTIMISTIC_REPORT_NODE_PREFIX}${Date.now()}`;
  const node: WorkspaceNode = {
    id: nodeId,
    type: "report",
    entityId: `${PENDING_ENTITY_PREFIX}${Date.now()}`,
    position: args.position,
    isDisabled: false,
    meta: {
      title: args.title.trim() || "New report",
      status: "processing",
    },
  };
  const edge: WorkspaceEdge = {
    id: `${nodeId}-edge`,
    sourceNodeId: args.parentNodeId,
    targetNodeId: nodeId,
  };

  return {
    nodeId,
    detail: {
      ...cleaned,
      nodes: [...cleaned.nodes, node],
      edges: [...cleaned.edges, edge],
    },
  };
}

/** Stamp the placeholder with the real backtest id so refresh can adopt it cleanly. */
export function stampOptimisticSimulationEntity(
  detail: WorkspaceDetail,
  args: { backtestId: string; title?: string; status?: WorkspaceNodeStatus | "pending" | "error" | "ready" }
): WorkspaceDetail {
  const backtestId = args.backtestId.trim();
  if (!backtestId) return detail;

  let stamped = false;
  const nodes = detail.nodes.map((node): WorkspaceNode => {
    if (!isOptimisticWorkspaceSimulationNodeId(node.id)) return node;
    if (!isPendingOptimisticSimulationEntityId(node.entityId)) return node;
    if (stamped) return node;
    stamped = true;
    const status: WorkspaceNodeStatus | undefined =
      args.status === "processing" || args.status === "draft" || args.status === "pending"
        ? "processing"
        : args.status === "failed" || args.status === "error"
          ? "failed"
          : args.status === "complete" || args.status === "saved"
            ? args.status
            : args.status === "ready"
              ? "complete"
              : undefined;
    return {
      ...node,
      entityId: backtestId,
      meta: {
        ...node.meta,
        title: args.title?.trim() || node.meta?.title || "New simulation",
        status,
      },
    };
  });

  return stamped ? { ...detail, nodes } : detail;
}

export function insertOptimisticWorkspaceSimulationNode(
  detail: WorkspaceDetail,
  args: {
    title: string;
    parentNodeId: string;
    position: WorkspaceNodePosition;
    /** Defaults to `simulation`; use `combined_simulation` when stacking under a combined sim. */
    nodeType?: "simulation" | "combined_simulation";
    meta?: {
      isRerun?: boolean;
      rerunOfEntityId?: string;
    };
  }
): { detail: WorkspaceDetail; nodeId: string } {
  const cleaned = stripOptimisticWorkspaceSimulationNodes(detail);
  const nodeId = `${OPTIMISTIC_SIMULATION_NODE_PREFIX}${Date.now()}`;
  const node: WorkspaceNode = {
    id: nodeId,
    type: args.nodeType ?? "simulation",
    entityId: `${PENDING_ENTITY_PREFIX}${Date.now()}`,
    position: args.position,
    isDisabled: false,
    meta: {
      title: args.title.trim() || "New simulation",
      status: "processing",
      ...(args.meta?.isRerun ? { isRerun: true } : {}),
      ...(args.meta?.rerunOfEntityId
        ? { rerunOfEntityId: args.meta.rerunOfEntityId }
        : {}),
    },
  };
  const edge: WorkspaceEdge = {
    id: `${nodeId}-edge`,
    sourceNodeId: args.parentNodeId,
    targetNodeId: nodeId,
  };

  return {
    nodeId,
    detail: {
      ...cleaned,
      nodes: [...cleaned.nodes, node],
      edges: [...cleaned.edges, edge],
    },
  };
}
